import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Select, Spin } from "antd";
import {
  copyBriefBankEntry,
  fetchBriefBankMatterFolders,
  searchBriefBankMatters,
} from "../../service/briefBank";
import {
  FilterLabel,
  MatterResult,
  MatterResults,
  Warning,
} from "./BriefBankStyled";

const matterLabel = (matter) =>
  matter?.display_name ||
  matter?.name ||
  matter?.client_name ||
  matter?.search_client_name ||
  `Matter ${matter?.id}`;

const normalizeList = (response, keys = []) => {
  const payload = response?.data?.data || response?.data || response || {};
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return Array.isArray(payload?.results) ? payload.results : [];
};

const apiError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

export default function BriefBankCopyModal({
  open,
  entry,
  onClose,
  onCopied,
}) {
  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [filename, setFilename] = useState("");
  const [searching, setSearching] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState("");
  const copyInFlightRef = useRef(false);

  useEffect(() => {
    setMatterQuery("");
    setMatterResults([]);
    setSelectedMatter(null);
    setFolders([]);
    setFolderId("");
    setFilename(
      open ? entry?.source_name || `${entry?.title || "Brief"}.docx` : ""
    );
    setSearching(false);
    setLoadingFolders(false);
    setCopying(false);
    copyInFlightRef.current = false;
    setError("");
  }, [open, entry?.id, entry?.source_name, entry?.title]);

  useEffect(() => {
    if (!open || selectedMatter || matterQuery.trim().length < 2) {
      setMatterResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await searchBriefBankMatters(matterQuery.trim());
        if (!cancelled) {
          setMatterResults(normalizeList(response, ["matters"]));
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiError(requestError, "Matter search failed."));
          setMatterResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [matterQuery, open, selectedMatter]);

  useEffect(() => {
    if (!selectedMatter?.id) {
      setFolders([]);
      setFolderId("");
      return;
    }
    let cancelled = false;
    setFolderId("");
    setFolders([]);
    setLoadingFolders(true);
    fetchBriefBankMatterFolders(selectedMatter.id)
      .then((response) => {
        if (!cancelled) setFolders(normalizeList(response, ["folders"]));
      })
      .catch((requestError) => {
        if (!cancelled) {
          setFolders([]);
          setError(apiError(requestError, "Could not load Matter folders."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFolders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMatter]);

  const selectedMatterLabel = useMemo(
    () => (selectedMatter ? matterLabel(selectedMatter) : ""),
    [selectedMatter]
  );

  const handleCopy = async () => {
    if (
      !entry?.id ||
      !selectedMatter?.id ||
      !filename.trim() ||
      copyInFlightRef.current
    ) {
      return;
    }
    copyInFlightRef.current = true;
    setCopying(true);
    setError("");
    try {
      const response = await copyBriefBankEntry(entry.id, {
        matter_id: selectedMatter.id,
        folder_id: folderId || null,
        filename: filename.trim(),
      });
      onCopied(response?.data || response);
    } catch (requestError) {
      setError(apiError(requestError, "The brief could not be copied."));
      copyInFlightRef.current = false;
      setCopying(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Use as template"
      onCancel={copying ? undefined : onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={copying}>
          Cancel
        </Button>,
        <Button
          key="copy"
          type="primary"
          loading={copying}
          disabled={copying || !selectedMatter?.id || !filename.trim()}
          onClick={handleCopy}
        >
          Copy to Matter
        </Button>,
      ]}
      destroyOnHidden
    >
      <Warning>
        This creates an independent Word copy. Review and replace all prior
        client facts, dates, and identifiers before using it.
      </Warning>

      <FilterLabel>
        Destination Matter
        <Input
          value={selectedMatter ? selectedMatterLabel : matterQuery}
          placeholder="Search by client, Matter name, or number"
          onChange={(event) => {
            setSelectedMatter(null);
            setFolderId("");
            setMatterQuery(event.target.value);
            setError("");
          }}
        />
      </FilterLabel>
      {searching && <Spin size="small" />}
      {matterResults.length > 0 && (
        <MatterResults>
          {matterResults.map((matter) => (
            <MatterResult
              key={matter.id}
              type="button"
              $selected={selectedMatter?.id === matter.id}
              onClick={() => {
                setFolderId("");
                setFolders([]);
                setSelectedMatter(matter);
                setMatterQuery(matterLabel(matter));
                setMatterResults([]);
                setError("");
              }}
            >
              {matterLabel(matter)}
            </MatterResult>
          ))}
        </MatterResults>
      )}

      <FilterLabel style={{ marginTop: 14 }}>
        Destination folder
        <Select
          value={folderId}
          loading={loadingFolders}
          disabled={!selectedMatter}
          onChange={setFolderId}
          options={[
            { value: "", label: "Matter root" },
            ...folders
              .filter((folder) => folder?.id)
              .map((folder) => ({
                value: folder.id,
                label: folder.path || folder.name || `Folder ${folder.id}`,
              })),
          ]}
        />
      </FilterLabel>

      <FilterLabel style={{ marginTop: 14 }}>
        New filename
        <Input
          value={filename}
          onChange={(event) => setFilename(event.target.value)}
          placeholder="Appellant Brief.docx"
        />
      </FilterLabel>

      {error && (
        <div role="alert" style={{ marginTop: 12, color: "#b42318" }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
